// src/components/enterprise/DeferredPanel.jsx
//
// A console tab an organization has put on hold (organizations/{id}.deferredConsoleTabs,
// see consoleTabs.js). The leave outlook and the financial model need a baseline of the
// organization's own check-ins and figures before a forecast or a dollar figure is honest;
// until then the tab stays in the sidebar with an "after 90 days" tag and opens this page
// instead of the edition's modeled example.

import React from 'react';
import { Clock, ArrowRight, Database } from 'lucide-react';
import { useEdition } from '../../data/editions/EditionContext';
import { useOrganization } from '../../context/OrganizationContext';

const COPY = {
    'cc-predictive': {
        what: 'the leave outlook',
        uses: [
            'Ninety days or more of members’ check-ins, by group',
            'The organization’s own sick calls, overtime and long-term absence, by group',
            'A baseline, so movement is measured against this organization and not a model of another'
        ]
    },
    'cc-roi': {
        what: 'the financial model',
        uses: [
            'Headcount and loaded labour cost',
            'Absence days, turnover and benefits use, by group where they exist',
            'Ninety days or more of members’ check-ins beside them'
        ]
    }
};

const DeferredPanel = ({ tab, onNavigate }) => {
    const edition = useEdition();
    const { orgData } = useOrganization();
    const copy = COPY[tab] || { what: 'this view', uses: ['Ninety days or more of the organization’s own data'] };
    const title = (edition.pageTitles && edition.pageTitles[tab]) || 'On hold';
    const who = orgData?.name || 'this organization';
    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-8">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-[var(--brand-blue)] shrink-0"><Clock size={22} /></div>
                    <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Switches on after 90 days of your own data</p>
                        <h2 className="text-xl font-black text-[var(--brand-deep)] mt-1">{title}</h2>
                        <p className="text-sm text-slate-600 mt-3 leading-relaxed max-w-2xl">
                            {`A forecast or a dollar figure built before ${who} has a baseline would be a model of some other organization, labelled or not. This view stays off until the figures below are on file, and then it reads ${copy.what} from them.`}
                        </p>
                    </div>
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {copy.uses.map((u) => (
                        <div key={u} className="bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm text-slate-700 leading-snug">{u}</div>
                    ))}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                    <button type="button" onClick={() => onNavigate && onNavigate('cc-data')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--brand-deep)] text-white text-sm font-semibold hover:opacity-90">
                        <Database size={16} /> Add figures in Data Intake <ArrowRight size={14} />
                    </button>
                    <button type="button" onClick={() => onNavigate && onNavigate('cc-overview')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                        Back to the overview
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeferredPanel;
